/**
 * Per-symbol cache of Exness session boundaries.
 *
 * The backend serves an unauthenticated JSON list at
 * /api/session_boundaries/{symbol}; we pre-parse it into the
 * ParsedBoundary form that the Weekly Behaviour band-rendering
 * code can binary-search.
 *
 * One symbol's boundaries don't change during a session (the
 * detector is part of the daily reconciliation job), so we fetch
 * lazily-once-per-symbol and cache. The Chart component dispatches
 * `fetchSessionBoundaries(symbol)` on mount / on symbol change.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import {
  parseSessionBoundary,
  type ParsedBoundary,
} from "@/state/selectors/effectiveWeeklyBehaviour";

interface SessionBoundariesState {
  bySymbol: Record<string, ParsedBoundary[]>;
  loading: Record<string, boolean>;
  error: Record<string, string | null>;
}

const initialState: SessionBoundariesState = {
  bySymbol: {},
  loading: {},
  error: {},
};

export const fetchSessionBoundaries = createAsyncThunk<
  { symbol: string; boundaries: ParsedBoundary[] },
  string,
  { state: { sessionBoundaries: SessionBoundariesState } }
>(
  "sessionBoundaries/fetch",
  async (symbol) => {
    const resp = await api.getSessionBoundaries(symbol);
    return {
      symbol,
      boundaries: resp.boundaries.map(parseSessionBoundary),
    };
  },
  {
    // Skip the request when the cache is already populated OR a
    // fetch is in flight. The condition runs BEFORE the request
    // fires, so this is a free dedup.
    condition: (symbol, { getState }) => {
      const s = getState().sessionBoundaries;
      return !s.bySymbol[symbol] && !s.loading[symbol];
    },
  },
);

const sessionBoundariesSlice = createSlice({
  name: "sessionBoundaries",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionBoundaries.pending, (state, action) => {
        state.loading[action.meta.arg] = true;
        state.error[action.meta.arg] = null;
      })
      .addCase(fetchSessionBoundaries.fulfilled, (state, action) => {
        const { symbol, boundaries } = action.payload;
        state.loading[symbol] = false;
        state.bySymbol[symbol] = boundaries;
      })
      .addCase(fetchSessionBoundaries.rejected, (state, action) => {
        const symbol = action.meta.arg;
        state.loading[symbol] = false;
        state.error[symbol] =
          action.error.message ?? "Failed to load session boundaries";
      });
  },
});

export default sessionBoundariesSlice.reducer;
